import { useCallback, useMemo } from "react";
import { Activation, ActivePage, Breadcrumb, MetaInterest } from "@/types";

const client = process.env.REACT_APP_CLIENT || "core";

export const defaultActivation: Activation = {
  campaign_id: "",
  client: client,
  campaign_name: "",
  campaign_type: "",
  created_at: "",
  campaign_date_start: "",
  campaign_date_end: "",
  platform: "",
  seat_id: "",
  audiences: [],
  status: "submitted",
};

/**
 * Build breadcrumbs for an activation page
 */
export const getActivationBreadcrumb = (
  activePage: ActivePage | null,
  activation: Activation | null,
  loading?: boolean,
  page?: string
): Breadcrumb[] => {
  const newActivation =
    !activation ||
    activation.campaign_id === "" ||
    activation.campaign_name === "";

  if (!activePage) return [];

  if (loading) return activePage.breadcrumbs;

  const activationPath = `${activePage.path}/${
    newActivation ? "new" : activation.campaign_id
  }`;
  const activationTitle = newActivation ? "New" : activation.campaign_name;
  const activationBreadcrumb = { title: activationTitle, path: activationPath };

  if (page) {
    const pagePath = `${activationPath}/${page}`;
    const pageBreadcrumb = { title: page, path: pagePath };

    return [...activePage.breadcrumbs, activationBreadcrumb, pageBreadcrumb];
  } else {
    return [...activePage.breadcrumbs, activationBreadcrumb];
  }
};

export const isActivationExpired = (activation: Activation) => {
  if (!activation.campaign_date_end || activation?.campaign_date_end === "")
    return false;

  const endDate = new Date(activation.campaign_date_end);
  const now = new Date();
  return endDate < now;
};

export const prepareGender = (gender: string) => {
  if (gender === "both") {
    return [0, 1];
  } else if (gender === "male") {
    return [0];
  } else {
    return [1];
  }
};

export const useInterestColor = (
  selection: MetaInterest[][],
  active: number,
  editMode: boolean
) => {
  // Map each interest to either 'active' or 'other'
  const membership = useMemo(() => {
    const map = new Map<number, "active" | "other">();
    selection.forEach((group, idx) => {
      for (const item of group) {
        if (!editMode) {
          map.set(item.meta_id, "other");
        } else if (idx === active) {
          map.set(item.meta_id, "active");
        } else if (!map.has(item.meta_id)) {
          map.set(item.meta_id, "other");
        }
      }
    });
    return map;
  }, [selection, active, editMode]);

  // A function to lookup the status for each interest
  const getStatus = useCallback(
    (id: number): "active" | "other" | "none" => membership.get(id) ?? "none",
    [membership]
  );

  // Assign a colour to the interest based on its status
  const getColor = useCallback(
    (id: number) => {
      const s = getStatus(id);
      if (s === "active") return "secondary";
      if (s === "other") return "info";
      return "default";
    },
    [getStatus]
  );

  return { getColor };
};

export const filterActivations = (
  activations: Activation[] | null,
  searchText?: string,
  filterBy?: string,
  sortBy?: string
) => {
  // Filter by search text
  let filteredActivations = activations?.filter((activation) =>
    activation.campaign_name
      .toLowerCase()
      .includes(searchText?.toLowerCase() || "")
  );

  // Filter by status
  if (filterBy) {
    filteredActivations = filteredActivations?.filter(
      (activation) => {
        if (filterBy === 'complete') {
          return (activation.status === filterBy) || (isActivationExpired(activation))
        } else {
          return (activation.status === filterBy) && (!isActivationExpired(activation))
        }
      }
    );
  }

  // Apply sorting
  if (sortBy) {
    if (sortBy === "name") {
      filteredActivations?.sort((a, b) =>
        a.campaign_name.localeCompare(b.campaign_name)
      );
    } else if (sortBy === "date") {
      filteredActivations?.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      );
    }
  } else {
    // Make sure the activations with no status are always shown first unless there is a sort
    filteredActivations = [
      ...(filteredActivations?.filter((x) => x.status === null) || []),
      ...(filteredActivations?.filter((x) => x.status !== null) || []),
    ];
  }

  return filteredActivations;
};
